import qs from 'query-string';
import { resolve, doorClient, ImResponse, conversationClient, messDoorClient } from '.';

export type LazadaReturnStatus = 'REQUEST' | 'PROCESS' | 'REFUND' | 'COMPLETE' | 'REFUSE';
export type MercadoReturnStatus = 'BUYER_PENDING' | 'MEDIATION';
export type KwaiReturnStatus = 'PENDING' | 'COMPLETE' | 'CLOSED';

export type ProcessStatus =
  | 'REQUEST'
  | 'PENDING'
  | 'PROCESS'
  | 'PROCESSED'
  | 'COMPLETE'
  | LazadaReturnStatus
  | MercadoReturnStatus;

export interface StatusHistory {
  returnStatus: Conversation.Trade.LazadaDisplayStatus;
  updateTimeMs: string;
}

export interface ReturnItem {
  itemImage: string[];
  returnQuantity: number;
  itemPrice: number;
  itemName: string;
  itemId: string;
  skuId: string;
  sellerSku: string;
  skuName: string;
  orderItemId?: string;
  returnOrderItemId?: string;
  displayStatus?: Conversation.Trade.LazadaDisplayStatus;
  returnStatusHistory?: StatusHistory[];
  reason?: string;
  dueDateMs?: string;
  operate?: Conversation.Trade.IReturnOperationType[];
  refundAmount?: number;
  /**
   * 弃单物流单号
   */
  returnTrackingNumber?: number;
  returnShipmentId: string;
  returnSubLineItems?: {
    itemImage: string[];
    itemName: string;
    itemId: string;
    skuId: string;
    skuName: string;
    orderItemId: string;
    subOrderLineItemId: string;
    returnOrderItemId: string;
    returnSubLineItemId: string;
    refundAmount: string;
    sellerSku: string;
  }[];
}

export interface QueryReturnListStateCountResp {
  total: number;
  pendingCount: number;
  processedCount: number;
  completeCount: number;
}

export const getReturnCancelCount = () => {
  return resolve(doorClient.get<ImResponse<{ total: number }>>('/trade/v1/countReturnCancelState'));
};

export interface GetTalkInfoByBuyerStoreBody {
  items: {
    internalStoreId?: number;
    internalBuyerId?: number;
    region: General.UpperCaseRegion;
    channel: General.PlatformUpperCase;
    externalStoreId: string;
    externalBuyerId: string;
  }[];
  needPhone?: boolean;
}

export const getTalkInfoByBuyerStore = (
  body: GetTalkInfoByBuyerStoreBody,
  signal?: AbortSignal,
) => {
  return resolve(
    doorClient.post<
      ImResponse<{
        aggTalks: Array<{
          talk: Conversation.Conversation;
          phone: Conversation.TalkPhone;
        }>;
      }>
    >('/aggregation/v1/getAggTalkByBuyerStore', body, { signal }),
  ).then((res) => {
    return {
      talkInfos: res.aggTalks.map((x) => ({ ...x.talk, phone: x.phone })),
    };
  });
};

export interface CreateConversationBody {
  externalBuyerId: string;
  buyerNick?: string;
  region?: General.UpperCaseRegion;
  channel: General.PlatformUpperCaseTunnel;
  storeId?: string;
  externalStoreId?: string;
  orderId?: string;
  tunnelAccountId?: string;
  sourceAction?: 'SEARCH_ORDER' | 'ORDER_MANAGEMENT' | 'RATING' | 'TODO';
}

export const createConversation = (body: CreateConversationBody) => {
  return resolve(
    conversationClient.post<ImResponse<Conversation.Conversation>>('conversations', body),
  );
};

export interface QueryCancelOrderParam {
  platform: General.PlatformUpperCase;
  orderId?: string;
  orderState?: 'LE_CHAT_IN_CANCEL' | 'LE_CHAT_CANCELED';
  createTimeBegin?: string;
  createTimeEnd?: string;
  pageNo?: string;
  pageSize?: string;
  // 当需要查处理中时传状态为LE_CHAT_IN_CANCEL，并将该值设为true，其他情况为false
  cancelBeingProcessed?: boolean;
  region?: General.UpperCaseRegion;
  internalStoreIdList?: string[];
  secondaryProcessStatus?: string;
}

export interface CancelItem {
  itemImages: string[];
  quantity: number;
  itemPrice: number;
  itemName: string;
  itemId: string;
  skuId: string;
}

export interface CancelOrder {
  orderId: string;
  storeName: string;
  buyerName: string;
  createTime: string;
  totalAmount: number;
  orderState: string;
  reason: string;
  orderItemInfos: CancelItem[];
  currency: string;
  unit: string;
  inCancelTime: string;
  externalStoreId: string;
  // externalBuyerId: string;
  buyerId: string;
  reverseOrderId: string;
  region: General.UpperCaseRegion;
  operationCancelUserId: string;
  operationCancelTime: string;
  operationCancelUserName: string;
  storeAliasName: string;
  storeId: string;
  cancelStatus: string;
  /** 取消订单可操作项 */
  operate: Conversation.Trade.IReturnOperationType[];
  internalTradeId: number;
  /**
   * 卖家备注(平台)
   */
  sellerMemos?: string;
  lechatSellerMemos?: string;
}

export const queryCancelOrderList = (params: QueryCancelOrderParam, signal?: AbortSignal) => {
  return resolve(
    doorClient.get<
      ImResponse<{
        data: CancelOrder[];
        total: number;
      }>
    >('/trade/v1/queryAssistantTrade', {
      params,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'none' }),
      signal,
    }),
  );
};

export interface ExportCancelOrderListData
  extends Omit<QueryCancelOrderParam, 'pageNo' | 'pageSize'> {
  timeZone: number;
}

export const exportCancelOrderList = (data: ExportCancelOrderListData) => {
  return resolve<ImResponse<undefined>>(doorClient.post('/trade/v1/exportCancelOrderList', data));
};

export const handleBuyerCancellation = (body: {
  externalStoreId: string;
  orderId: string;
  platform: string;
  isAccept: boolean;
  // TT 原因ID
  reasonId?: string;
  // TT 卖家备注
  comment?: string;
  // TT 图片
  image?: string[];
}) => {
  return doorClient.post<ImResponse<undefined>>('/trade/v1/processBuyerCancelOrder', body);
};

export interface QueryCancelStateOrigin {
  total: number;
  inCancelCount: number;
  toProcessedCancelCount: number;
  canceledCount: number;
}

export const queryCancelState = (body: {
  platform: General.Platform;
  createTimeBegin?: number;
  createTimeEnd?: number;
  orderId?: string;
  cancelBeingProcessed?: boolean;
  region?: General.UpperCaseRegion;
  internalStoreIdList?: string[];
}) => {
  return resolve(
    messDoorClient.post<ImResponse<QueryCancelStateOrigin>>('/trade/v1/countCancelState', body),
  ).then((resp) => {
    const transformed: QueryReturnListStateCountResp = {
      total: resp.total,
      pendingCount: resp.inCancelCount,
      processedCount: resp.toProcessedCancelCount,
      completeCount: resp.canceledCount,
    };
    return transformed;
  });
};

export const updateReturnOrderStatus = (body: {
  region: string;
  platform: string;
  returnSn: string;
}) => {
  return resolve(doorClient.post<ImResponse<any>>('/trade/v1/updateReturn', body));
};
export const updateCancelOrderStatus = (body: {
  orderId: string;
  reverseOrderId: string;
  region: string;
  platform: string;
}) => {
  return resolve(doorClient.post<ImResponse<any>>('/trade/v1/updateCancelOrderToProcessed', body));
};

export interface ReturnOrderListItemV2 {
  returnSn: string;
  orderSn: string;
  returnStatus: string;
  reason: string;
  reassessedRequestReason?: string;
  textReason: string;
  textReasonTrans: Record<'zh' | 'en', string>;
  returnImage: string[];
  returnVideo: { thumbnailUrl: string; videoUrl: string }[];
  returnItemData: ReturnItem[];
  operate: Conversation.Trade.IReturnOperationType[];
  sellerType: Conversation.Trade.ISellerType;
  storeType: Conversation.Trade.IStoreType;
  region: General.Region;
  storeName: string;
  externalBuyerId: string;
  buyerNick: string;
  refundAmount: number;
  currency: string;
  unit: string;
  returnCreateTime: string;
  returnUpdateTime: string;
  platform: string;
  externalStoreId: string;
  storeAlias: string;
  internalStoreId: string;
  returnType: Conversation.Trade.IReturnType;
  operationLog: Conversation.Trade.IReturnOperationLog[];
  offerCounterLimit: number;
  allowOffer: Conversation.Trade.IAllowOffer[];
  /** 最新议价的金额，可能是卖家提出也可能是买家提出的，根据退款单状态确定 */
  latestOfferAmount: number;
  sellerProofStatus: string;
  sellerCompensationStatus: string;
  negotiationStatus: string;
  displayStatus: Conversation.Trade.IDisplayStatus;
  /** 整个退款单最终的处理时限 */
  dueDateMs: string;
  /** 买家退货发物流后，卖家最终可操作的时限 */
  returnSellerDueDateMs: string;
  /** 议价自动同意时间 */
  offerDueDateMs: string;
  /** 卖家提交证据的deadline时间 */
  sellerEvidenceDeadlineMs: string;
  /** 卖家要求赔偿的截止日期 */
  sellerCompensationDueDateMs: string;
  /** 买家寄送截止日期 */
  returnShipDueDateMs: string;
  /** 是否包含预购商品 */
  containsPreOrderItem?: boolean;
  phone: string;
  tradeCreateTimeMs: string;
  /** 订单状态 */
  leChatStatus: Conversation.Trade.LeChatTradeStatus;
  /** 退款单所属订单的订单表的主键 */
  internalTradeId: number;
  /**
   * 卖家备注(平台)
   */
  sellerMemos?: string;
  lechatSellerMemos?: string;
  compensationAmount: number;
  /**
   * Mercado 平台
   * 影响声誉
   */
  reputation?: boolean;
  hasIncentive?: boolean;
  /**
   * 弃单物流单号
   */
  returnTrackingNumber: string;
  returnShipmentId: string;
  /**
   * Mercado 退款原因
   */
  reasonName?: string;
  partiallyRefundAmount?: string;
  preDispute: {
    failReasonCode: number;
    content: string;
  };
  internalReturnId?: string;
  reverseTag?: 'DISPUTE_IN_PROGRESS'[];
}

export enum SecondaryProcessStatus {
  // all 仅仅前端使用，当选择all时不传给后端
  ALL = 'ALL',
  REFUND_APPROVED_BY_SHOPEE = 'REFUND_APPROVED_BY_SHOPEE',
  RETURN_APPROVED_BY_SHOPEE = 'RETURN_APPROVED_BY_SHOPEE',
  NEGOTIATION_PENDING_RESPONSE = 'NEGOTIATION_PENDING_RESPONSE',
  EVIDENCE_REQUESTED = 'EVIDENCE_REQUESTED',
  VALIDATION_IN_PROGRESS = 'VALIDATION_IN_PROGRESS',
}

// 退单原因枚举
export type QueryReturnReason =
  | 'BROKEN_PRODUCTS'
  | 'CHANGE_MIND'
  | 'CHANGE_OF_MIND'
  | 'DAMAGED_OTHERS'
  | 'DIFFERENT_DESCRIPTION'
  | 'EXPECTATION_FAILED'
  | 'EXPIRED_PRODUCT'
  | 'FUNCTIONAL_DMG'
  | 'ITEM_FAKE'
  | 'ITEM_MISSING'
  | 'ITEM_NOT_FIT'
  | 'LONG_DELIVERY_TIME'
  | 'NOT_RECEIPT'
  | 'OUTER_DAMAGED_PACKAGE'
  | 'PHYSICAL_DMG'
  | 'SELLER_SENT_WRONG_ITEM'
  | 'SLIGHT_SCRATCH_DENTS'
  | 'SPILLED_CONTENTS'
  | 'SUSPICIOUS_PARCEL'
  | 'USED'
  | 'WRONG_ADDRESS'
  | 'WRONG_ITEM'
  | 'WRONG_ORDER_INFO';

export interface QueryReturnListV2Req {
  orderSn?: string;
  returnSn?: string;
  // 弃单物流单号
  returnTrackingNumber?: string;
  page: {
    pageNo: number;
    pageSize: number;
  };
  processStatus?: Exclude<ProcessStatus, 'all'>;
  applicationStart?: string;
  applicationEnd?: string;
  // platform: General.PlatformUpperCase;
  platform: General.Platform;
  storeType?: Conversation.Trade.IStoreType | '';
  sellerType?: Conversation.Trade.ISellerType | '';
  region?: General.UpperCaseRegion;
  internalStoreIdList?: string[];
  secondaryProcessStatus?: SecondaryProcessStatus;
  dueTimeEnd?: string;
  reason?: QueryReturnReason[];
  operate?: string[];
  displayStatus?: string[];
  returnType?: string;
  refundAmountStart?: string;
  refundAmountEnd?: string;
  tradeCreateTimeStart?: string;
  tradeCreateTimeEnd?: string;
  buyerNick?: string;
  itemName?: string;
  skuName?: string;
  sellerSku?: string;
}

/** 查询退货单列表V2
 POST /oversea-door/trade/v2/queryReturnList
 接口ID：21584854
 接口地址：https://www.apifox.cn/web/project/625489/apis/api-21584854 */
export const queryReturnListV2 = async (body: QueryReturnListV2Req, signal?: AbortSignal) => {
  return resolve(
    doorClient.post<ImResponse<{ total: number; returnOrderDataList: ReturnOrderListItemV2[] }>>(
      '/trade/v2/queryReturnList',
      body,
      { signal },
    ),
  );
};

export interface GetReturnCancelCountV2Resp {
  total: number;
  shopeeReturnCount: number;
  lazadaReturnCount: number;
  shopeeCancelCount: number;
  lazadaCancelCount: number;
  shopeeTotalCount: number;
  lazadaTotalCount: number;
  tiktokReturnCount: number;
  tiktokCancelCount: number;
  tiktokTotalCount: number;
  tiktokUsReturnCount: number;
  tiktokUsCancelCount: number;
  tiktokUsTotalCount: number;
  mercadoTotalCount: number;
  mercadoClaimCount: number;
  mercadoReturnCount: number;
  kwaiTotalCount: number;
}

/** 统计未处理的 退款单+取消订单 V2
 GET /oversea-door/trade/v2/countReturnCancelState
 接口ID：21688543
 接口地址：https://www.apifox.cn/web/project/625489/apis/api-21688543 */
export const getReturnCancelCountV2 = () => {
  return resolve(
    messDoorClient.get<ImResponse<GetReturnCancelCountV2Resp>>('/trade/v2/countReturnCancelState'),
  ).then((resp) => {
    const transformed: GetReturnCancelCountV2Resp = {
      total: resp.total,
      shopeeReturnCount: resp.shopeeReturnCount || 0,
      shopeeCancelCount: resp.shopeeCancelCount || 0,
      lazadaReturnCount: resp.lazadaReturnCount || 0,
      lazadaCancelCount: resp.lazadaCancelCount || 0,
      shopeeTotalCount: resp.shopeeTotalCount || 0,
      lazadaTotalCount: resp.lazadaTotalCount || 0,
      tiktokReturnCount: resp.tiktokReturnCount || 0,
      tiktokCancelCount: resp.tiktokCancelCount || 0,
      tiktokTotalCount: resp.tiktokTotalCount || 0,
      tiktokUsReturnCount: resp.tiktokUsReturnCount || 0,
      tiktokUsCancelCount: resp.tiktokUsCancelCount || 0,
      tiktokUsTotalCount: resp.tiktokUsTotalCount || 0,
      mercadoTotalCount: resp.mercadoTotalCount || 0,
      mercadoClaimCount: resp.mercadoClaimCount || 0,
      mercadoReturnCount: resp.mercadoReturnCount || 0,
      kwaiTotalCount: resp.kwaiTotalCount || 0,
    };

    return transformed;
  });
};

export interface QueryReturnListStateCountV2Req {
  orderSn?: string;
  returnSn?: string;
  applicationStart?: string;
  applicationEnd?: string;
  platform: General.Platform;
  storeType?: Conversation.Trade.IStoreType | '';
  sellerType?: Conversation.Trade.ISellerType | '';
  region?: General.UpperCaseRegion;
  internalStoreIdList?: string[];
  dueTimeEnd?: string;
}

export interface QueryReturnListStateCountV2Resp extends QueryReturnListStateCountResp {
  requestCount: number;
  processCount: number;
  buyerPendingCount?: number;
  mediationCount?: number;
  closedCount?: number;
}

/** 统计退货单列表各状态的数量-V2
 POST /oversea-door/trade/v2/queryReturnState
 接口ID：21410836
 接口地址：https://www.apifox.cn/web/project/625489/apis/api-21410836 */
export const queryReturnListStateCountV2 = (body: QueryReturnListStateCountV2Req) => {
  return resolve(
    messDoorClient.post<ImResponse<QueryReturnListStateCountV2Resp>>(
      '/trade/v2/queryReturnState',
      body,
    ),
  );
};

/**
 * 场景: shopee 退货退款 -> 上传证据
 */
export const uploadReturnEvidence = (body: {
  externalTradeId: string;
  externalStoreId: string;
  returnSn: string;
  image: string[];
  description: string;
}) => {
  return resolve(doorClient.post<ImResponse<any>>('/trade/v1/return/proof', body));
};

export type ExistedEvidence = {
  description: string;
  image: string[];
  video: {
    thumbnailUrl: string;
    videoUrl: string;
  }[];
};
/**
 * 场景: shopee 退货退款 -> 查询上传的证据
 */
export const queryReturnEvidence = (params: {
  externalTradeId: string;
  externalStoreId: string;
  returnSn: string;
}) => {
  return resolve(
    doorClient.get<ImResponse<ExistedEvidence>>('/trade/v1/return/proof', {
      params,
    }),
  );
};

export const getOfferOptions = (params: { returnExternalId: string; platform: 'MERCADO' }) => {
  return resolve(
    doorClient.get<
      ImResponse<{
        availableOffers: {
          amount: string;
          percentage: string;
          currency: string;
          unit: string;
        }[];
      }>
    >('/trade/v1/availableOffers', { params }),
  );
};

// 手动刷新退单列表
export const refreshReturnList = (internalReturnId: string[]) => {
  return resolve<ImResponse<undefined>>(
    messDoorClient.post('/trade/v1/refreshReturnList', { internalReturnId }),
  );
};

interface ExportReturnListData extends Omit<QueryReturnListV2Req, 'page'> {
  timeZone: number;
}

// 导出退单列表
export const exportReturnList = (data: ExportReturnListData) => {
  return resolve<ImResponse<undefined>>(doorClient.post('/trade/v1/exportReturnList', data));
};
